import type { FC } from 'react';
import { useMemo, useRef } from 'react';

import {
  Box,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';

import CustomCheckbox from '@/components/Checkbox';
import { fetchSymbolData } from '@/components/SymbolsPreview/api/fetchSymbolData';
import type { SymbolKey } from '@/entities/symbol/types';
import FactsheetDialogButton from '@/features/Factsheet/components/FactsheetDialogButton';
import FactsheetDialogButtonBase from '@/features/Factsheet/components/FactsheetDialogButton/FactsheetDialogButtonBase';
import useSelectionFunctions from '@/features/SymbolSelector/hooks/useSelectionFunctions';
import usePeriodAbsFormatter from '@/hooks/usePeriodAbsFormatter';
import type { CommoditiesTableRowMetadata } from '@/pages/Markets/MarketsComponents/types';

export type SymbolPreviewItem = {
  symbol: SymbolKey;
  label?: string;
  date?: string;
  periodAbs?: string;
  metadata?: CommoditiesTableRowMetadata;
};

const SymbolsPreview: FC<{
  items: SymbolPreviewItem[];
  enableQuery?: boolean;
  forChat?: boolean;
}> = ({ items, enableQuery = true, forChat = false }) => {
  const { addSymbolsToSelection, areAllSymbolsSelected, removeSymbolsFromSelection } =
    useSelectionFunctions();
  const allSymbols = useMemo(
    () => items.map(item => item.symbol).filter(symbol => !!symbol.Code),
    [items]
  );

  const filteredItems = useMemo(() => items.filter(item => !!item.symbol.Code), [items]);

  const allSelected = useMemo(
    () => areAllSymbolsSelected(allSymbols),
    [allSymbols, areAllSymbolsSelected]
  );
  const boxRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    getScrollElement: () => boxRef.current,
    estimateSize: () => 50,
    overscan: 3,
    count: allSymbols.length,
    getItemKey: index => filteredItems[index]?.symbol?.Code ?? index
  });

  const virtualRows = rowVirtualizer.getVirtualItems();
  const colSpan = forChat ? 6 : 7;
  return (
    <Box
      ref={boxRef}
      sx={{
        my: '12px',
        mb: forChat ? 0 : undefined,
        overflowY: 'auto',
        backgroundColor: forChat ? 'var(--GX-Tile-Dark, #0D1228)' : undefined
      }}
    >
      <Table
        stickyHeader
        aria-label='purchases'
        size='small'
        sx={{
          '& td, & th': { px: 0 },
          '& th:first-of-type, & td:first-of-type': { pl: 2 },
          '& th:last-of-type, & td:last-of-type': { pr: 2 }
        }}
      >
        <TableHead
          sx={{ '& th': { backgroundColor: forChat ? 'var(--GX-Tile-Dark, #0D1228)' : 'inherit' } }}
        >
          <TableRow>
            <TableCell sx={{ pr: 2, width: '10%' }}>
              {forChat && (
                <Box
                  aria-label='select all'
                  role='button'
                  sx={{
                    color: 'var(--GX-Highlight, #E84E0F)',
                    fontSize: 10,
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    if (allSelected) {
                      removeSymbolsFromSelection(allSymbols);
                    } else {
                      addSymbolsToSelection(allSymbols);
                    }
                  }}
                >
                  {allSelected ? 'Deselect all' : 'Select all'}
                </Box>
              )}
            </TableCell>
            <TableCell sx={{ pr: 2, width: '12%' }}>
              <Typography sx={theme => ({ fontSize: 10, color: theme.palette.table.infoRowText })}>
                Alias
              </Typography>
            </TableCell>
            <TableCell sx={{ pr: 2, width: '30%' }}>
              <Typography sx={theme => ({ fontSize: 10, color: theme.palette.table.infoRowText })}>
                Title
              </Typography>
            </TableCell>
            {!forChat && (
              <TableCell sx={{ pr: 2, width: '7%' }}>
                <Typography
                  sx={theme => ({ fontSize: 10, color: theme.palette.table.infoRowText })}
                >
                  PeriodAbs
                </Typography>
              </TableCell>
            )}
            <TableCell
              align='right'
              sx={{ pr: 2, width: '5%' }}
            >
              <Typography sx={theme => ({ fontSize: 10, color: theme.palette.table.infoRowText })}>
                Currency
              </Typography>
            </TableCell>
            <TableCell
              align='right'
              sx={{ pr: 2, width: '7%' }}
            >
              <Typography sx={theme => ({ fontSize: 10, color: theme.palette.table.infoRowText })}>
                Units
              </Typography>
            </TableCell>
            <TableCell
              align='right'
              sx={{ width: '10%', whiteSpace: 'nowrap' }}
            >
              <Typography sx={theme => ({ fontSize: 10, color: theme.palette.table.infoRowText })}>
                Info
              </Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell
              colSpan={colSpan}
              sx={{ p: 0, height: virtualRows[0]?.start ?? 0 }}
            />
          </TableRow>

          {virtualRows.map(vRow => {
            const { symbol, label, date, periodAbs, metadata } = filteredItems[vRow.index];
            return (
              <SymbolRow
                key={vRow.key}
                date={date ?? 'today'}
                enableQuery={enableQuery}
                forChat={forChat}
                label={label}
                measureElement={rowVirtualizer.measureElement}
                metadata={metadata}
                periodAbs={periodAbs}
                rowIndex={vRow.index}
                symbol={symbol}
              />
            );
          })}
          <TableRow>
            <TableCell
              colSpan={colSpan}
              sx={{
                p: 0,
                height: Math.max(0, rowVirtualizer.getTotalSize() - (virtualRows.at(-1)?.end ?? 0))
              }}
            />
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
};

const SymbolRow: FC<{
  enableQuery?: boolean;
  symbol: SymbolKey;
  label?: string;
  date: string;
  periodAbs?: string;
  metadata?: CommoditiesTableRowMetadata;
  forChat?: boolean;
  rowIndex: number;
  measureElement: (element: HTMLElement | null) => void;
}> = ({
  symbol,
  label,
  date,
  metadata,
  periodAbs,
  enableQuery = true,
  forChat = false,
  rowIndex,
  measureElement
}) => {
  const { isSymbolSelected, toggleSymbolInSelection } = useSelectionFunctions();
  const checked = isSymbolSelected(symbol);
  const { data, isLoading } = useQuery({
    queryKey: ['symbol', symbol, date],
    queryFn: () => fetchSymbolData(symbol, date),
    enabled: enableQuery && !periodAbs
  });

  const stripedBg = forChat && rowIndex % 2 === 1 ? 'rgb(18, 24, 54)' : undefined;

  const { formatPeriodAbs } = usePeriodAbsFormatter();

  const row = useMemo(() => {
    let alias = metadata?.Alias ?? data?.alias;
    if (!alias || alias === '') {
      alias = symbol.Code;
    }
    return {
      alias,
      title: metadata?.Title ?? data?.title,
      periodAbs: formatPeriodAbs(periodAbs ?? data?.periodAbs),
      currency: metadata?.Currency ?? data?.currency,
      units: metadata?.Units ?? data?.units
    };
  }, [metadata, data, symbol, formatPeriodAbs, periodAbs]);

  if (isLoading) {
    return (
      <TableRow sx={{ backgroundColor: stripedBg }}>
        <TableCell colSpan={forChat ? 6 : 7}>
          <Skeleton
            height='23px'
            width='100%'
          />
        </TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow
      ref={measureElement}
      data-index={rowIndex}
      sx={{ backgroundColor: stripedBg }}
    >
      <TableCell sx={{ pr: 0.8, width: '10%' }}>
        <Box sx={{ mt: '-1px' }}>
          <CustomCheckbox
            checked={checked}
            onChange={() => {
              toggleSymbolInSelection(symbol);
            }}
          />
          {label && (
            <Typography
              sx={{ pl: 0.5 }}
              variant='caption'
            >
              {label}
            </Typography>
          )}
        </Box>
      </TableCell>
      <TableCell sx={{ pr: 2, width: '12%' }}>
        <Typography variant='caption'>{row.alias}</Typography>
      </TableCell>
      <TableCell sx={{ pr: 2, width: '30%' }}>
        <FactsheetDialogButtonBase code={symbol.Code}>
          {' '}
          <Typography
            sx={{ textDecoration: 'underline', textAlign: 'start' }}
            variant='caption'
          >
            {row.title}
          </Typography>
        </FactsheetDialogButtonBase>
      </TableCell>
      {!forChat && (
        <TableCell sx={{ pr: 2, width: '7%', whiteSpace: 'nowrap' }}>
          <Typography variant='caption'>{row.periodAbs}</Typography>
        </TableCell>
      )}
      <TableCell
        align='right'
        sx={{ pr: 2, width: '7%' }}
      >
        <Typography variant='caption'>{row.currency}</Typography>
      </TableCell>
      <TableCell
        align='right'
        sx={{ pr: 2, width: '7%' }}
      >
        <Typography variant='caption'>{row.units}</Typography>
      </TableCell>
      <TableCell
        align='right'
        sx={{ width: '10%' }}
      >
        <Typography variant='caption'>
          <FactsheetDialogButton code={symbol.Code} />
        </Typography>
      </TableCell>
    </TableRow>
  );
};

export default SymbolsPreview;
